import { Icon } from '@/components/ui/Icon';
import { getButtonColor, type ColorButtonKey } from '@/lib/colors';
import { mdiLoading } from '@mdi/js';
import cx from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';

export interface ButtonProps {
  label?: string;
  icon?: string;
  iconSize?: string | number;
  href?: string;
  target?: string;
  type?: 'button' | 'submit' | 'reset';
  color?: ColorButtonKey;
  small?: boolean;
  outline?: boolean;
  active?: boolean;
  disabled?: boolean;
  roundedFull?: boolean;
  loading?: boolean;
  isGrouped?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
  title?: string;
}

export const Button: React.FC<React.PropsWithChildren<ButtonProps>> = ({
  label,
  icon,
  iconSize,
  href,
  target,
  type = 'button',
  color = 'white',
  small = false,
  outline = false,
  active = false,
  disabled = false,
  roundedFull = false,
  loading = false,
  isGrouped = false,
  onClick,
  className,
  children,
  title,
  ...props
}) => {
  const componentClassString = cx(
    'inline-flex',
    'justify-center',
    'items-center',
    'whitespace-nowrap',
    'focus:outline-none',
    'transition-all',
    'focus:ring-2',
    'duration-200',
    'border',
    'font-medium',
    disabled || loading ? 'cursor-not-allowed' : 'cursor-pointer',
    roundedFull ? 'rounded-full' : 'rounded-lg',
    getButtonColor(color, outline, !disabled && !loading, active),
    isGrouped && 'mr-3 last:mr-0 mb-3',
    !label && icon
      ? small
        ? 'p-2'
        : 'p-3'
      : small
        ? 'text-sm px-3 py-2'
        : 'text-base px-4 py-3',
    (disabled || loading) && (outline ? 'opacity-50' : 'opacity-70'),
    className,
  );

  const iconElement = loading ? (
    <Icon
      path={mdiLoading}
      size={iconSize || (small ? 16 : 20)}
      className='animate-spin'
    />
  ) : icon ? (
    <Icon path={icon} size={iconSize || (small ? 16 : 20)} />
  ) : null;

  const componentChildren = (
    <>
      {iconElement}
      {label && (
        <span
          className={small && iconElement ? 'ml-1' : iconElement ? 'ml-2' : ''}
        >
          {label}
        </span>
      )}
      {children}
    </>
  );

  if (href && !disabled) {
    return (
      <Link
        to={href}
        target={target}
        className={componentClassString}
        title={title}
      >
        {componentChildren}
      </Link>
    );
  }

  return (
    <button
      className={componentClassString}
      type={type}
      title={title}
      disabled={disabled || loading}
      onClick={onClick}
      {...props}
    >
      {componentChildren}
    </button>
  );
};
